import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Activity, ArrowLeft, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { createMotorTest } from '@/utils/api';
import { toast } from 'sonner';

export default function TremorTest({ patient }) {
  const navigate = useNavigate();
  const [testState, setTestState] = useState('ready'); // ready, running, finished
  const [timeLeft, setTimeLeft] = useState(10);
  const [tremorData, setTremorData] = useState([]);
  const timerRef = useRef(null);
  const dataIntervalRef = useRef(null);

  const startTest = () => {
    setTestState('running');
    setTimeLeft(10);
    setTremorData([]);

    // Simula letture accelerometro
    dataIntervalRef.current = setInterval(() => {
      const mockReading = {
        x: (Math.random() - 0.5) * 2,
        y: (Math.random() - 0.5) * 2,
        z: (Math.random() - 0.5) * 2,
        timestamp: Date.now(),
      };
      setTremorData((prev) => [...prev, mockReading]);
    }, 100);

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          clearInterval(dataIntervalRef.current);
          setTestState('finished');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const calculateTremorScore = () => {
    if (tremorData.length === 0) return 100;

    // Calcola intensità media del tremore
    const avgIntensity = tremorData.reduce((sum, reading) => {
      const magnitude = Math.sqrt(reading.x ** 2 + reading.y ** 2 + reading.z ** 2);
      return sum + magnitude;
    }, 0) / tremorData.length;

    // Punteggio inversamente proporzionale all'intensità (meno tremore = punteggio più alto)
    const score = Math.max(0, Math.min(100, 100 - avgIntensity * 30));
    return Math.round(score);
  };

  const saveTest = async () => {
    const score = calculateTremorScore();

    try {
      await createMotorTest({
        patient_id: patient.id,
        tipo: 'tremor',
        punteggio: score,
        durata: 10,
        dettagli: {
          readings_count: tremorData.length,
          avg_magnitude: tremorData.reduce((sum, r) => sum + Math.sqrt(r.x ** 2 + r.y ** 2 + r.z ** 2), 0) / tremorData.length,
        },
      });

      toast.success('Test salvato con successo!');
      setTimeout(() => navigate('/test-motori'), 2000);
    } catch (error) {
      console.error('Errore nel salvataggio del test:', error);
      toast.error('Errore nel salvataggio del test');
    }
  };

  useEffect(() => {
    if (testState === 'finished') {
      saveTest();
    }
  }, [testState]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (dataIntervalRef.current) clearInterval(dataIntervalRef.current);
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="p-4 flex items-center justify-between border-b border-slate-200 bg-white">
        <Button
          data-testid="btn-back"
          variant="ghost"
          onClick={() => navigate('/test-motori')}
          className="min-h-[48px] rounded-2xl"
        >
          <ArrowLeft className="w-6 h-6 mr-2" />
          Indietro
        </Button>
        <h1 className="text-xl font-bold text-slate-900">Test Tremore</h1>
        <div className="w-24"></div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <AnimatePresence mode="wait">
          {testState === 'ready' && (
            <motion.div
              key="ready"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="text-center max-w-md"
            >
              <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center mx-auto mb-6 soft-shadow-lg">
                <Activity className="w-12 h-12 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Pronto per il Test?</h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Tieni il telefono fermo davanti a te per 10 secondi. Cerca di rimanere il più stabile possibile.
              </p>
              <Button
                data-testid="btn-start-tremor-test"
                onClick={startTest}
                className="w-full min-h-[64px] text-lg font-semibold rounded-2xl bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white shadow-lg hover:shadow-xl transition-all duration-300 active:scale-[0.98]"
              >
                Inizia il Test
              </Button>
            </motion.div>
          )}

          {testState === 'running' && (
            <motion.div
              key="running"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center max-w-md w-full"
            >
              <div className="mb-12">
                <motion.div
                  key={timeLeft}
                  initial={{ scale: 1.2 }}
                  animate={{ scale: 1 }}
                  className="text-8xl font-bold text-green-600 mb-2"
                >
                  {timeLeft}
                </motion.div>
                <p className="text-xl text-slate-600">Tieni fermo il telefono</p>
              </div>

              <motion.div
                animate={{
                  scale: [1, 1.1, 1],
                  opacity: [0.6, 1, 0.6],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
                className="w-48 h-48 rounded-full bg-gradient-to-br from-green-400 to-green-600 mx-auto soft-shadow-lg"
              >
                <div className="w-full h-full flex items-center justify-center">
                  <Activity className="w-20 h-20 text-white" />
                </div>
              </motion.div>

              <p className="text-sm text-slate-500 mt-8">Rilevazioni: {tremorData.length}</p>
            </motion.div>
          )}

          {testState === 'finished' && (
            <motion.div
              key="finished"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center max-w-md"
            >
              <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mx-auto mb-6 soft-shadow-lg">
                <TrendingUp className="w-12 h-12 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-2">Test Completato!</h2>
              <div className="text-7xl font-bold text-green-600 my-6">{calculateTremorScore()}</div>
              <p className="text-xl text-slate-600 mb-2">Punteggio Stabilità</p>
              <p className="text-base text-slate-500 mb-8">Rilevazioni analizzate: {tremorData.length}</p>
              <p className="text-sm text-slate-500">Salvataggio in corso...</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
